import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo } from "react";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";

const DetailCellRendererLoanException = ({ data, node, api }) => {
  const rowId = node.id;

  useEffect(() => {
    return () => {
      if (!api.isDestroyed()) {
        console.log("removing detail grid info with id: ", rowId);
        api.removeDetailGridInfo(rowId);
      }
    };
  }, []);

  const quantitativeGuidelinesColDefs = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
      sortable: true,
      filter: true,
    },
    {
      field: "description",
      headerName: "Description",
      width: 250,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_text",
      headerName: "Guideline Text",
      width: 300,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_type",
      headerName: "Type",
      width: 120,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_category",
      headerName: "Category",
      width: 140,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_source_material",
      headerName: "Source Material",
      width: 180,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_page_reference",
      headerName: "Page Reference",
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_value",
      headerName: "Value",
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_quantitative_value",
      headerName: "Quantitative Value",
      width: 170,
      sortable: true,
      filter: true,
    },
    {
      field: "value_unit",
      headerName: "Unit",
      width: 100,
      sortable: true,
      filter: true,
    },
    {
      field: "value_calculation",
      headerName: "Calculation",
      width: 250,
      sortable: true,
      filter: true,
    },
    {
      field: "value_calculation_description",
      headerName: "Calculation Description",
      width: 300,
      sortable: true,
      filter: true,
    },
  ];

  // Column definitions for qualitative_guidelines array
  const qualitativeGuidelinesColDefs = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
      sortable: true,
      filter: true,
    },
    {
      field: "description",
      headerName: "Description",
      width: 250,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_text",
      headerName: "Guideline Text",
      width: 300,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_type",
      headerName: "Type",
      width: 120,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_category",
      headerName: "Category",
      width: 140,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_source_material",
      headerName: "Source Material",
      width: 180,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_page_reference",
      headerName: "Page Reference",
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_value",
      headerName: "Value",
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      field: "guideline_qualitative_value",
      headerName: "Qualitative Value",
      width: 170,
      sortable: true,
      filter: true,
    },
    {
      field: "value_reference",
      headerName: "Value Reference",
      width: 180,
      sortable: true,
      filter: true,
    },
    {
      field: "value_reference_description",
      headerName: "Reference Description",
      width: 300,
      sortable: true,
      filter: true,
    },
  ];

  const requiredReportsColDefs = [
    {
      field: "report_name",
      headerName: "Report Name",
      width: 200,
      sortable: true,
      filter: true,
    },
    {
      field: "report_description",
      headerName: "Description",
      width: 300,
      sortable: true,
      filter: true,
    },
    {
      field: "report_type",
      headerName: "Report Type",
      width: 150,
      sortable: true,
      filter: true,
    },
    {
      field: "approved_vendors",
      headerName: "Approved Vendors",
      width: 250,
      sortable: true,
      filter: true,
      valueFormatter: (params) =>
        Array.isArray(params.value) ? params.value.join(", ") : params.value,
    },
    {
      field: "max_report_age",
      headerName: "Max Age",
      width: 120,
      sortable: true,
      filter: true,
    },
  ];

  // Column definitions for document_requirements array
  const documentRequirementsColDefs = [
    {
      field: "document_name",
      headerName: "Document Name",
      width: 200,
      sortable: true,
      filter: true,
    },
    {
      field: "document_description",
      headerName: "Description",
      width: 300,
      sortable: true,
      filter: true,
    },
    {
      field: "document_type",
      headerName: "Document Type",
      width: 180,
      sortable: true,
      filter: true,
    },
    {
      field: "max_document_age",
      headerName: "Max Age",
      width: 120,
      sortable: true,
      filter: true,
    },
  ];

  // Column definitions for guideline_definitions array
  const guidelineDefinitionsColDefs = [
    {
      field: "defined_term",
      headerName: "Term",
      width: 120,
      sortable: true,
      flexGrow: 0,
      filter: true,
    },
    {
      field: "definition",
      headerName: "Definition",
      width: 400,
      sortable: true,
      filter: true,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        lineHeight: "1.6",
      },
    },
  ];

  // -----------

  const rowDataQuantitativeGuidelines = useMemo(() => {
    return data.quantitative_guidelines || [];
  }, [data.quantitative_guidelines]);

  const rowDataQualitativeGuidelines = useMemo(() => {
    return data.qualitative_guidelines || [];
  }, [data.qualitative_guidelines]);

  const rowDataRequiredReports = useMemo(() => {
    return data.required_reports || [];
  }, [data.required_reports]);

  const rowDataDocumentRequirements = useMemo(() => {
    return data.document_requirements || [];
  }, [data.document_requirements]);

  const rowDataGuidelineDefinitions = useMemo(() => {
    return data.guideline_definitions || [];
  }, [data.guideline_definitions]);

  return (
    <Box
      sx={{
        p: 4,
        background: grey[100],
        overflow: "auto",
        flexDirection: "column",
        display: "flex",
        gap: 4,
      }}
      role="gridcell"
      className="full-width-panel"
    >
      <Flx fw jb ac gap={4}>
        <Flx gap={1} fw sx={{ justifyContent: "space-between" }}>
          <DisplayItem
            label="Allowed Borrower Entities"
            value={data.allowed_borrower_entities}
          />
          <DisplayItem
            label="Prohibited Borrower Entities"
            value={data.prohibited_borrower_entities}
          />
          <DisplayItem
            label="Allowed Subject Property States"
            value={data.allowed_subject_property_states}
          />
          <DisplayItem
            label="Maximum Loan Amounts"
            value={data.maximum_loan_amounts}
          />
          <DisplayItem
            label="Minimum Loan Amounts"
            value={data.minimum_loan_amounts}
          />
          <DisplayItem label="Loan Product" value={data.loan_product} />
        </Flx>
        <Flx ac gap={1}>
          <Button>Some Action</Button>
          <Button>Another Action</Button>
        </Flx>
      </Flx>
      {/* <Divider /> */}
      <FlxGrid width="620px" gap={2} wrap sx={{ width: "100%" }}>
        <TitledDataTable
          title="Quantitative Guidelines"
          columnDefs={quantitativeGuidelinesColDefs}
          rowData={rowDataQuantitativeGuidelines}
        />
        <TitledDataTable
          title="Qualitative Guidelines"
          columnDefs={qualitativeGuidelinesColDefs}
          rowData={rowDataQualitativeGuidelines}
        />
        <TitledDataTable
          title="Required Reports"
          columnDefs={requiredReportsColDefs}
          rowData={rowDataRequiredReports}
        />
        <TitledDataTable
          title="Document Requirements"
          columnDefs={documentRequirementsColDefs}
          rowData={rowDataDocumentRequirements}
        />
        <TitledDataTable
          title="Guideline Definitions"
          columnDefs={guidelineDefinitionsColDefs}
          rowData={rowDataGuidelineDefinitions}
        />
      </FlxGrid>
    </Box>
  );
};

const DisplayItem = ({ label, value }) => {
  return (
    <Flx column>
      <Txt bold>{label}:</Txt>
      <Txt>{value}</Txt>
    </Flx>
  );
};
const TitledDataTable = ({ title, columnDefs, rowData }) => {
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 120,
      suppressHeaderFilterButton: true,
    };
  }, []);

  return (
    <Flx
      column
      sx={{
        border: `1px solid ${grey[300]}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1,
          borderBottom: `1px solid ${grey[300]}`,
          background: grey[100],
        }}
      >
        <Txt bold>{title}</Txt>
      </Box>
      <AgGridTableWrapper size={4}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          headerHeight={34}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
        />
      </AgGridTableWrapper>
    </Flx>
  );
};

export default DetailCellRendererLoanException;
